import { useEffect, useRef, useCallback } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { useWebRTC, CallType, SignalingMessage } from '../hooks/useWebRTC'
import { WSMessageTypes } from '../hooks/useWebSocket'
import { useSharedWebSocket } from '../contexts/WebSocketContext'

export default function CallPage() {
  const { recipientId } = useParams<{ recipientId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { sendRTCOffer, sendRTCAnswer, sendICECandidate, sendMessage, lastMessage, isConnected } = useSharedWebSocket()

  const callType = (searchParams.get('type') as CallType) || 'audio'
  const isIncoming = searchParams.get('incoming') === 'true'

  const localVideoRef = useRef<HTMLVideoElement>(null)
  const remoteVideoRef = useRef<HTMLVideoElement>(null)

  // currentCallType ref to access in callback without re-creating
  const currentCallTypeRef = useRef<CallType>(callType)

  // Track if we've started the call to avoid multiple starts
  const hasStartedCallRef = useRef(false)

  // Handle signaling messages - use correct WebSocket methods
  const handleSignalingMessage = useCallback((message: SignalingMessage) => {
    console.log('[CallPage] handleSignalingMessage received:', message.type)
    console.log('[CallPage] recipientId:', recipientId)

    if (!recipientId) {
      console.error('[CallPage] No recipientId, cannot send signaling message')
      return
    }

    switch (message.type) {
      case 'offer':
        console.log('[CallPage] Processing offer, sdp exists:', !!message.sdp, 'sdp.sdp exists:', !!message.sdp?.sdp)
        console.log('[CallPage] SDP length:', message.sdp?.sdp?.length || 0)
        if (message.sdp?.sdp) {
          console.log('[CallPage] Calling sendRTCOffer to:', recipientId)
          sendRTCOffer(recipientId, message.sdp.sdp, currentCallTypeRef.current)
          console.log('[CallPage] sendRTCOffer called')
        } else {
          console.error('[CallPage] No SDP in offer message!')
        }
        break
      case 'answer':
        console.log('[CallPage] Processing answer')
        if (message.sdp?.sdp) {
          sendRTCAnswer(recipientId, message.sdp.sdp)
        }
        break
      case 'ice-candidate':
        console.log('[CallPage] Processing ICE candidate')
        if (message.candidate) {
          sendICECandidate(recipientId, message.candidate)
        }
        break
      case 'end-call':
        console.log('[CallPage] Processing end-call')
        sendMessage({ type: 'rtc_hangup', payload: { target_id: recipientId } })
        break
      case 'reject-call':
        console.log('[CallPage] Processing reject-call')
        sendMessage({ type: 'rtc_reject', payload: { target_id: recipientId } })
        break
    }
  }, [recipientId, sendRTCOffer, sendRTCAnswer, sendICECandidate, sendMessage])

  const {
    callState,
    callType: currentCallType,
    localStream,
    remoteStream,
    isMuted,
    isVideoOff,
    formattedDuration,
    error,
    startCall,
    answerCall,
    handleAnswer,
    handleIceCandidate,
    endCall,
    toggleMute,
    toggleVideo,
    switchCamera,
  } = useWebRTC(recipientId || '', handleSignalingMessage)

  // Update call type ref when it changes
  useEffect(() => {
    currentCallTypeRef.current = currentCallType
  }, [currentCallType])

  // Start call on mount, or answer if incoming
  // Wait for WebSocket to be connected first
  useEffect(() => {
    console.log('[CallPage] Mount effect - recipientId:', recipientId, 'isIncoming:', isIncoming, 'callType:', callType, 'isConnected:', isConnected, 'hasStarted:', hasStartedCallRef.current)

    if (!recipientId) {
      console.log('[CallPage] No recipientId, returning early')
      return
    }

    // Don't start the call multiple times
    if (hasStartedCallRef.current) {
      console.log('[CallPage] Call already started, skipping')
      return
    }

    // Wait for WebSocket to be connected before starting outgoing calls
    if (!isIncoming && !isConnected) {
      console.log('[CallPage] WebSocket not connected yet, waiting...')
      return
    }

    if (isIncoming) {
      console.log('[CallPage] Incoming call, looking for stored SDP')
      // Get stored SDP from sessionStorage (set by IncomingCallModal)
      const storedSdp = sessionStorage.getItem('incoming_call_sdp')
      const storedCallType = sessionStorage.getItem('incoming_call_type') as CallType

      if (storedSdp) {
        console.log('[CallPage] Found stored SDP, answering call')
        hasStartedCallRef.current = true
        const sdp: RTCSessionDescriptionInit = { type: 'offer', sdp: storedSdp }
        answerCall(sdp, storedCallType || callType)

        // Clear stored data
        sessionStorage.removeItem('incoming_call_sdp')
        sessionStorage.removeItem('incoming_call_type')
      } else {
        console.log('[CallPage] No stored SDP for incoming call')
      }
    } else {
      console.log('[CallPage] Outgoing call, WebSocket connected, calling startCall')
      hasStartedCallRef.current = true
      startCall(callType)
    }
  }, [recipientId, callType, isIncoming, startCall, answerCall, isConnected])

  // Handle incoming signaling messages from WebSocket
  useEffect(() => {
    if (!lastMessage) return

    const { type, payload } = lastMessage
    const data = payload?.data as Record<string, unknown> | undefined

    // Handle different RTC message types
    switch (type) {
      case WSMessageTypes.RTC_OFFER:
        // Incoming call offer
        if (data?.sdp) {
          const sdp: RTCSessionDescriptionInit = { type: 'offer', sdp: data.sdp as string }
          const incomingCallType = (data.call_type as CallType) || 'audio'
          answerCall(sdp, incomingCallType)
        }
        break
      case WSMessageTypes.RTC_ANSWER:
        // Answer to our call
        if (data?.sdp) {
          const sdp: RTCSessionDescriptionInit = { type: 'answer', sdp: data.sdp as string }
          handleAnswer(sdp)
        }
        break
      case WSMessageTypes.RTC_CANDIDATE:
        // ICE candidate from peer
        if (data) {
          handleIceCandidate(data as RTCIceCandidateInit)
        }
        break
      case 'rtc_hangup':
      case 'rtc_reject':
        navigate(-1)
        break
    }
  }, [lastMessage, handleAnswer, handleIceCandidate, answerCall, navigate])

  // Attach local stream to video element
  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream
    }
  }, [localStream])

  // Attach remote stream to video element
  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream
    }
  }, [remoteStream])

  // Handle end call
  const handleEndCall = () => {
    endCall()
    navigate(-1)
  }

  // Get call status text
  const getStatusText = () => {
    switch (callState) {
      case 'calling':
        return 'Calling...'
      case 'ringing':
        return 'Ringing...'
      case 'connected':
        return formattedDuration
      case 'failed':
        return error || 'Call failed'
      case 'ended':
        return 'Call ended'
      default:
        return ''
    }
  }

  const isVideoCall = currentCallType === 'video'

  return (
    <div className={`min-h-screen flex flex-col ${isVideoCall ? 'bg-black' : 'bg-gradient-to-b from-primary to-primary-dark'}`}>
      {/* Video views */}
      {isVideoCall && (
        <>
          {/* Remote video (full screen) */}
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
          />

          {/* Local video (picture-in-picture) */}
          <div className="absolute top-20 right-4 w-32 h-44 rounded-xl overflow-hidden shadow-lg z-10">
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              className={`w-full h-full object-cover ${isVideoOff ? 'hidden' : ''}`}
            />
            {isVideoOff && (
              <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
            )}
          </div>
        </>
      )}

      {/* Header */}
      <header className="relative z-20 flex items-center justify-between p-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Encryption badge */}
        <div className="flex items-center gap-1 px-3 py-1 bg-white/10 rounded-full">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-secure-green" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <span className="text-xs text-white">End-to-end encrypted</span>
        </div>

        {/* Switch camera (video only) */}
        {isVideoCall && (
          <button
            onClick={switchCamera}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        )}
      </header>

      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center relative z-10 px-4">
        {/* Avatar (audio call only) */}
        {!isVideoCall && (
          <div className="mb-8">
            <div className="w-32 h-32 bg-white/20 rounded-full flex items-center justify-center shadow-lg">
              <span className="text-5xl text-white font-semibold">
                {recipientId?.charAt(0).toUpperCase()}
              </span>
            </div>
            {callState === 'connected' && (
              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-secure-green rounded-full border-2 border-white" />
            )}
          </div>
        )}

        {/* Recipient name */}
        <h1 className="text-2xl font-bold text-white mb-2">
          {recipientId}
        </h1>

        {/* Call status */}
        <p className={`text-lg ${callState === 'failed' ? 'text-red-300' : 'text-white/80'}`}>
          {getStatusText()}
        </p>

        {/* Call type indicator */}
        <div className="mt-4 flex items-center gap-2 text-white/60">
          {isVideoCall ? (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <span>Video Call</span>
            </>
          ) : (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <span>Voice Call</span>
            </>
          )}
        </div>
      </main>

      {/* Call controls */}
      <footer className="relative z-20 pb-12 pt-8">
        <div className="flex items-center justify-center gap-6">
          {/* Mute button */}
          <button
            onClick={toggleMute}
            className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
              isMuted ? 'bg-red-500' : 'bg-white/20 hover:bg-white/30'
            }`}
          >
            {isMuted ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" clipRule="evenodd" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              </svg>
            )}
          </button>

          {/* End call button */}
          <button
            onClick={handleEndCall}
            className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-colors shadow-lg"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.517l2.257-1.128a1 1 0 00.502-1.21L9.228 3.683A1 1 0 008.279 3H5z" />
            </svg>
          </button>

          {/* Video toggle (video call only) */}
          {isVideoCall && (
            <button
              onClick={toggleVideo}
              className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
                isVideoOff ? 'bg-red-500' : 'bg-white/20 hover:bg-white/30'
              }`}
            >
              {isVideoOff ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              )}
            </button>
          )}
        </div>
      </footer>
    </div>
  )
}
